/**
 * Tiny direct caller for Cognito User Pool APIs that the signed-in user
 * can invoke on their own behalf with their AccessToken (no AWS SigV4
 * needed — these endpoints accept the AccessToken as the auth proof).
 *
 * Today: just ChangePassword (DE-150). The same pattern works for
 * UpdateUserAttributes, DeleteUser, etc. if we ever need those.
 *
 * The endpoint hostname comes from `cognito-idp.<region>.amazonaws.com`;
 * we derive the region from VITE_COGNITO_AUTHORITY so a single bundle
 * works across regions if we ever multi-region the pool.
 */

const AUTHORITY = import.meta.env.VITE_COGNITO_AUTHORITY as string | undefined;

function cognitoRegion(): string {
  // VITE_COGNITO_AUTHORITY looks like
  //   https://cognito-idp.eu-central-1.amazonaws.com/<userPoolId>
  // Region is the segment after "cognito-idp." up to the next ".".
  const match = AUTHORITY?.match(/cognito-idp\.([^.]+)\.amazonaws\.com/);
  if (!match) {
    throw new Error('VITE_COGNITO_AUTHORITY missing or malformed');
  }
  return match[1]!;
}

async function callCognito(
  action: string,
  body: Record<string, unknown>,
): Promise<unknown> {
  const region = cognitoRegion();
  const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: { message?: string; __type?: string } = {};
    try {
      detail = await res.json();
    } catch {
      /* fall through to plain text */
    }
    const message =
      detail.message ?? `Cognito ${action} failed (${res.status})`;
    const err = new Error(message) as Error & { __type?: string; status?: number };
    err.__type = detail.__type;
    err.status = res.status;
    throw err;
  }
  return res.json().catch(() => ({}));
}

/**
 * Change the signed-in user's own password.
 *
 * DE-150 — uses Cognito's ChangePassword (current + new) so the user
 * proves they know the existing password. Distinct from
 * AdminSetUserPassword which would require admin creds and bypass the
 * old-password check.
 */
export async function changeOwnPassword(
  accessToken: string,
  previousPassword: string,
  proposedPassword: string,
): Promise<void> {
  await callCognito('ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}
